<template>
  <Navbar :title="navTitle" :class="{'show' : showNavbar}">
    <template v-slot:close>
      <div @click="handleClose" class="close">Cerrar</div>
    </template>

    <template v-slot:content>

      <!-- ── Paso 1: formulario de pago ───────────────────────── -->
      <template v-if="step === 'pay' && !data.paid">
        <div v-if="data.client" class="bill-detail">
          <div class="bill-detail__amount">
            {{ fmt(data.total) }}
          </div>
          <div v-if="amountEdited" class="bill-detail__diff">
            Monto a registrar: <strong>{{ fmt(document.total) }}</strong>
          </div>
          <div class="bill-detail__grid">
            <div class="bill-detail__item" v-if="data.client.name || data.client.alias">
              <span class="bill-detail__label">Inquilino</span>
              <span class="bill-detail__value">{{ data.client.alias || data.client.name }}</span>
            </div>
            <div class="bill-detail__item" v-if="data.client.local">
              <span class="bill-detail__label">Propiedad</span>
              <span class="bill-detail__value">{{ data.client.local.description }}</span>
            </div>
            <div class="bill-detail__item" v-if="data.deadline">
              <span class="bill-detail__label">Vencimiento</span>
              <span class="bill-detail__value">{{ moment(data.deadline).lang('es').format('DD[/]MMMM[/]YYYY') }}</span>
            </div>
            <div class="bill-detail__item" v-if="data.client.currency">
              <span class="bill-detail__label">Moneda</span>
              <span class="bill-detail__value">{{ data.client.currency.name || data.client.currency.code }}</span>
            </div>
          </div>
        </div>

        <div class="form-group">
          <label for="paymentTotal">Monto del pago</label>
          <input v-model.number="document.total" class="input" type="number" id="paymentTotal" step="0.01" min="0.01">
          <span v-if="validationErrors.total" class="error">{{ validationErrors.total }}</span>
        </div>

        <div class="form-group">
          <label for="paymentDate">Fecha de pago</label>
          <v-date-picker
            ref="paymentDate"
            id="paymentDate"
            v-model="document.paymentDate"
            color="red"
            is-expanded
            :timezone="`America/Lima`"
          />
          <span v-if="validationErrors.paymentDate" class="error">{{ validationErrors.paymentDate }}</span>
        </div>

        <div class="form-group">
          <label for="observation">Observación</label>
          <textarea v-model="document.observation" class="textarea" id="observation" rows="2" placeholder="Nota opcional sobre el pago"></textarea>
        </div>

        <!-- Lo que el inquilino ya envió desde el app. Hasta ahora estos
             comprobantes existían pero eran invisibles justo en la pantalla
             donde se decide cuánto se cobró. -->
        <div v-if="clientVoucherList.length" class="form-group">
          <label>Comprobantes enviados por el inquilino</label>
          <div class="media-grid">
            <div v-for="(img, index) in clientVoucherList" :key="'cv' + index" class="media">
              <img :src="img" alt="Comprobante del inquilino">
              <div @click="openImg(img)" class="media__open">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div class="form-group">
          <input @change="updateImage" class="file" ref="uploadVoucher" type="file" accept="image/*" multiple>
          <button class="btn btn--outline btn--w100" @click="$refs.uploadVoucher.click()">Adjuntar comprobantes</button>
          <div v-if="previews.length" class="previews">
            <div v-for="(img, index) in previews" :key="index" class="previews__item">
              <img :src="img" alt="Voucher">
              <span class="previews__remove" @click="removeImage(index)">&times;</span>
            </div>
          </div>
        </div>
      </template>

      <!-- ── Paso 2: nueva letra por la diferencia ─────────────── -->
      <template v-if="step === 'diff'">
        <div class="bill-detail">
          <div class="bill-detail__amount bill-detail__amount--paid">
            {{ fmt(savedPaid) }}
            <span class="bill-detail__badge">Pagado</span>
          </div>
          <div class="bill-detail__diff-summary">
            <div class="bill-detail__diff-row">
              <span>Total de la letra</span>
              <span>{{ fmt(data.total) }}</span>
            </div>
            <div class="bill-detail__diff-row">
              <span>Monto pagado</span>
              <span>{{ fmt(savedPaid) }}</span>
            </div>
            <div class="bill-detail__diff-row bill-detail__diff-row--total">
              <span>Diferencia pendiente</span>
              <strong>{{ fmt(diffAmount) }}</strong>
            </div>
          </div>
        </div>

        <p class="pay-diff__question">
          ¿Quieres crear una nueva letra por <strong>{{ fmt(diffAmount) }}</strong>?
        </p>

        <div class="form-group">
          <label for="diffDeadline">Fecha de vencimiento de la nueva letra</label>
          <v-date-picker
            ref="diffDeadline"
            id="diffDeadline"
            v-model="diffDeadline"
            color="red"
            is-expanded
            :timezone="`America/Lima`"
          />
          <span v-if="validationErrors.diffDeadline" class="error">{{ validationErrors.diffDeadline }}</span>
        </div>
      </template>

      <!-- ── Vista de letra ya pagada ──────────────────────────── -->
      <template v-if="data.paid">
        <div class="bill-detail">
          <div class="bill-detail__amount bill-detail__amount--paid">
            {{ fmt(data.total) }}
            <span class="bill-detail__badge">Pagado</span>
          </div>
          <div class="bill-detail__grid">
            <div class="bill-detail__item" v-if="data.client.name || data.client.alias">
              <span class="bill-detail__label">Inquilino</span>
              <span class="bill-detail__value">{{ data.client.alias || data.client.name }}</span>
            </div>
            <div class="bill-detail__item" v-if="data.client.local">
              <span class="bill-detail__label">Propiedad</span>
              <span class="bill-detail__value">{{ data.client.local.description }}</span>
            </div>
            <div class="bill-detail__item">
              <span class="bill-detail__label">Fecha de pago</span>
              <span class="bill-detail__value">{{ moment(document.paymentDate).lang('es').format('DD[/]MMMM[/]YYYY') }}</span>
            </div>
            <div class="bill-detail__item" v-if="data.client.currency">
              <span class="bill-detail__label">Moneda</span>
              <span class="bill-detail__value">{{ data.client.currency.name || data.client.currency.code }}</span>
            </div>
          </div>
          <div class="bill-detail__obs" v-if="document.observation">
            <span class="bill-detail__label">Observación</span>
            <p>{{ document.observation }}</p>
          </div>
        </div>
        <div v-if="voucherList.length" class="media-grid">
          <div v-for="(img, index) in voucherList" :key="index" class="media">
            <img :src="img" alt="Voucher de pago">
            <div @click="openImg(img)" class="media__open">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                <polyline points="15 3 21 3 21 9"></polyline>
                <line x1="10" y1="14" x2="21" y2="3"></line>
              </svg>
            </div>
          </div>
        </div>

        <!-- Los que mandó el inquilino desde el app. En la letra ya pagada no se
             mostraban: el listado los contaba —"1 comprobante · del inquilino"—
             y al abrirla no había nada que ver. -->
        <div v-if="paidClientVouchers.length" class="form-group">
          <label>Comprobantes enviados por el inquilino</label>
          <div class="media-grid">
            <div v-for="(img, index) in paidClientVouchers" :key="'pcv' + index" class="media">
              <img :src="img" alt="Comprobante del inquilino">
              <div @click="openImg(img)" class="media__open">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                     stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                  <polyline points="15 3 21 3 21 9"></polyline>
                  <line x1="10" y1="14" x2="21" y2="3"></line>
                </svg>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- ── Verificación automática del comprobante ───────────────── -->
      <div v-if="data.voucherAudit" class="voucher-audit">
        <span class="bill-detail__label">Verificación del comprobante</span>
        <VoucherStatus :audit="data.voucherAudit" />
        <span v-if="data.voucherAudit.detail" class="voucher-status__detail">
          {{ data.voucherAudit.detail }}
        </span>
        <span v-if="resolved" class="voucher-status__detail">
          Dada por revisada por {{ resolved.by }}<template v-if="resolved.note"> — {{ resolved.note }}</template>
        </span>

        <div class="voucher-audit__actions">
          <button v-if="canResolve" class="btn btn--outline btn--sm"
                  @click="showResolve = !showResolve">Dar por revisada</button>
          <button v-if="resolved" class="btn btn--outline btn--sm"
                  :disabled="resolving" @click="reopen">Reabrir</button>
          <button class="btn btn--outline btn--sm" :disabled="rechecking" @click="recheck">
            {{ rechecking ? 'Verificando…' : 'Volver a verificar' }}
          </button>
        </div>

        <div v-if="showResolve" class="voucher-audit__resolve">
          <label for="resolveNote">Por qué se da por buena (opcional)</label>
          <input v-model="resolveNote" class="input" id="resolveNote" maxlength="200"
                 placeholder="Ej: el depósito cubre también el otro local">
          <button class="btn btn--sm" :disabled="resolving" @click="resolve">
            {{ resolving ? 'Guardando…' : 'Confirmar' }}
          </button>
        </div>
      </div>

    </template>

    <template v-slot:footer>
      <!-- Paso 1 -->
      <button v-if="step === 'pay' && !data.paid"
              :disabled="progress" :class="{'is-loading': progress}"
              @click="save" class="btn">
        <span v-if="!progress">Registrar pago</span>
      </button>

      <!-- Paso 2 -->
      <template v-if="step === 'diff'">
        <button :disabled="progress" :class="{'is-loading': progress}"
                @click="createDiffBill" class="btn">
          <span v-if="!progress">Crear letra por {{ fmt(diffAmount) }}</span>
        </button>
        <button :disabled="progress" @click="skipDiff" class="btn btn--outline" style="margin-top:8px;width:100%">
          No crear, cerrar
        </button>
      </template>
    </template>
  </Navbar>
  <Shadow :class="{'show' : showNavbar}"/>
</template>

<script>
import Navbar from '@/components/Navbar'
import Shadow from '@/components/Shadow'
import VoucherStatus from '@/components/VoucherStatus'
import moment from 'moment'
import * as yup from 'yup'
import { db, FieldValue, Timestamp, storage, functions, auth } from '@/firebase/firebaseConfig'

yup.setLocale({
  mixed: { required: 'Este campo es obligatorio' },
  number: { positive: 'Debe ser positivo' }
})

export default {
  name: 'Pay',
  components: { Navbar, Shadow, VoucherStatus },
  props: {
    showNavbar: { type: Boolean, default: false },
    data: {
      type: Object,
      default: () => ({ client: { currency: { code: '' } } })
    }
  },
  emits: ['update', 'added', 'close', 'audit'],
  data () {
    return {
      step: 'pay',
      document: {},
      diffDeadline: null,
      savedUpdate: null,
      savedPaid: 0,
      progress: false,
      rechecking: false,
      resolving: false,
      showResolve: false,
      resolveNote: '',
      paySchema: yup.object().shape({
        total: yup.number().required().positive('Debe ser mayor a 0').label('Monto del pago'),
        paymentDate: yup.date().required().label('Fecha de pago')
      }),
      diffSchema: yup.object().shape({
        diffDeadline: yup.date().required().label('Fecha de vencimiento')
      }),
      validationErrors: {},
      attachments: [],
      previews: [],
      moment
    }
  },
  computed: {
    navTitle () {
      if (this.step === 'diff') return 'Nueva letra — diferencia'
      return 'Registrar pago'
    },
    currency () {
      return this.data.client && this.data.client.currency && this.data.client.currency.code
        ? this.data.client.currency.code
        : 'PEN'
    },
    amountEdited () {
      return this.document.total !== undefined && this.document.total !== this.data.total
    },
    diffAmount () {
      const diff = (this.data.total || 0) - (this.savedPaid || 0)
      return Math.round(diff * 100) / 100
    },
    voucherList () {
      if (this.document.vouchers && this.document.vouchers.length) return this.document.vouchers
      if (this.document.voucher) return [this.document.voucher]
      return []
    },
    resolved () {
      return (this.data.voucherAudit && this.data.voucherAudit.resolved) || null
    },
    canResolve () {
      // Sólo tiene sentido cerrar a mano lo que está alertando y nadie cerró aún.
      return !!this.data.voucherAudit && this.data.voucherAudit.status === 'alert' && !this.resolved
    },
    clientVoucherList () {
      return (this.data.clientVouchers && this.data.clientVouchers.length) ? this.data.clientVouchers : []
    },
    // Sin repetir los que ya salen arriba: la misma imagen puede haber quedado
    // guardada en las dos listas.
    paidClientVouchers () {
      return this.clientVoucherList.filter(u => !this.voucherList.includes(u))
    }
  },
  watch: {
    showNavbar (value) {
      if (value) {
        this.step = 'pay'
        this.validationErrors = {}
        this.previews = []
        this.attachments = []
        this.diffDeadline = null
        this.savedUpdate = null
        this.savedPaid = 0
        if (this.data.paid) {
          this.document = { ...this.data }
        } else {
          this.document = { total: this.data.total }
        }
      }
    }
  },
  methods: {
    fmt (amount) {
      return Intl.NumberFormat('en-US', { style: 'currency', currency: this.currency }).format(amount || 0)
    },
    handleClose () {
      if (this.step === 'diff') {
        this.skipDiff()
      } else {
        this.$emit('close')
      }
    },

    // ── Paso 1: guardar el pago ────────────────────────────────
    async save () {
      try {
        this.progress = true
        this.validationErrors = {}
        await this.paySchema.validate({ ...this.document }, { abortEarly: false })

        const obj = {
          total: this.document.total,
          paymentDate: this.document.paymentDate,
          paid: true,
          updatedAt: FieldValue.serverTimestamp(),
          vouchers: [],
          observation: this.document.observation || ''
        }

        if (this.attachments.length) {
          obj.vouchers = await Promise.all(this.attachments.map(f => this.submitFile(f)))
        }

        await db
          .collection('clients').doc(this.data.client.id)
          .collection('bills').doc(this.data.id)
          .update({ ...obj })

        obj.id = this.data.id
        this.savedUpdate = obj
        this.savedPaid = this.document.total

        // Si el monto pagado es menor al original → paso 2
        if (this.document.total < this.data.total) {
          this.step = 'diff'
        } else {
          this.$emit('update', obj)
          this.$emit('close')
        }
      } catch (err) {
        if (err.inner) {
          err.inner.forEach(e => { if (e.path) this.validationErrors[e.path] = e.message })
        } else {
          this.$toast.error('Error al registrar pago')
        }
      } finally {
        this.progress = false
      }
    },

    // ── Paso 2: crear letra por la diferencia ─────────────────
    async createDiffBill () {
      try {
        this.validationErrors = {}
        await this.diffSchema.validate({ diffDeadline: this.diffDeadline }, { abortEarly: false })

        this.progress = true

        const newBill = {
          total: this.diffAmount,
          deadline: Timestamp.fromDate(new Date(this.diffDeadline)),
          paid: false,
          client: this.data.client,
          currentContract: this.data.currentContract || null,
          observation: 'Diferencia de pago parcial',
          vouchers: [],
          createdAt: FieldValue.serverTimestamp(),
          state: true
        }

        const ref = await db
          .collection('clients').doc(this.data.client.id)
          .collection('bills').add(newBill)

        this.$emit('added', {
          id: ref.id,
          ...newBill,
          deadline: new Date(this.diffDeadline),
          createdAt: new Date()
        })
        this.$emit('update', this.savedUpdate)
        this.$emit('close')
        this.$toast.success('Nueva letra creada por ' + this.fmt(this.diffAmount))
      } catch (err) {
        if (err.inner) {
          err.inner.forEach(e => { if (e.path) this.validationErrors[e.path] = e.message })
        } else {
          this.$toast.error('Error al crear la nueva letra')
        }
      } finally {
        this.progress = false
      }
    },

    // ── Paso 2: cerrar sin crear nueva letra ──────────────────
    skipDiff () {
      this.$emit('update', this.savedUpdate)
      this.$emit('close')
    },

    updateImage (input) {
      if (input.target.files) {
        Array.from(input.target.files).forEach(file => {
          this.attachments.push(file)
          const reader = new FileReader()
          reader.onload = e => this.previews.push(e.target.result)
          reader.readAsDataURL(file)
        })
        this.$refs.uploadVoucher.value = ''
      }
    },
    removeImage (index) {
      this.attachments.splice(index, 1)
      this.previews.splice(index, 1)
    },
    submitFile (doc) {
      return new Promise((resolve, reject) => {
        const name = Math.random().toString(36).substr(1, 20) + '.' + doc.name.split('.').pop()
        storage.ref(`vouchers/${name}`).put(doc)
          .then(snap => snap.ref.getDownloadURL().then(resolve))
          .catch(reject)
      })
    },
    openImg (img) { window.open(img, '_blank') },

    // Cierra la alerta a mano. El motor se equivoca —lo midió la calibración— y
    // sin esta salida la única forma de callar un falso positivo sería borrar la
    // letra o subir otro comprobante. Queda registrado quién y por qué: no es
    // borrar la alerta, es dejar dicho que alguien la miró y la dio por buena.
    async resolve () {
      try {
        this.resolving = true
        const by = (auth.currentUser && (auth.currentUser.email || auth.currentUser.uid)) || 'desconocido'
        const resolved = { by, at: Timestamp.now(), note: (this.resolveNote || '').trim() }
        await db.collection('clients').doc(this.data.client.id)
          .collection('bills').doc(this.data.id)
          .update({ voucherAlert: false, 'voucherAudit.resolved': resolved })
        this.$emit('audit', {
          id: this.data.id,
          voucherAudit: { ...this.data.voucherAudit, resolved },
          voucherAlert: false
        })
        this.showResolve = false
        this.resolveNote = ''
        this.$toast.success('Letra dada por revisada')
      } catch (e) {
        this.$toast.error('No se pudo marcar como revisada')
      } finally {
        this.resolving = false
      }
    },

    // Deshace lo anterior: vuelve a dejar la alerta como la dejó el motor.
    async reopen () {
      try {
        this.resolving = true
        const wasAlert = this.data.voucherAudit && this.data.voucherAudit.status === 'alert'
        await db.collection('clients').doc(this.data.client.id)
          .collection('bills').doc(this.data.id)
          .update({ voucherAlert: !!wasAlert, 'voucherAudit.resolved': FieldValue.delete() })
        const audit = { ...this.data.voucherAudit }
        delete audit.resolved
        this.$emit('audit', { id: this.data.id, voucherAudit: audit, voucherAlert: !!wasAlert })
        this.$toast.success('Alerta reabierta')
      } catch (e) {
        this.$toast.error('No se pudo reabrir la alerta')
      } finally {
        this.resolving = false
      }
    },

    // Relee los comprobantes con Vision. Se usa cuando se adjuntó una foto mejor
    // o cuando el veredicto quedó en "ilegible" y se quiere reintentar.
    async recheck () {
      try {
        this.rechecking = true
        const callable = functions.httpsCallable('vouchers-recheckBill')
        const { data: result } = await callable({
          clientId: this.data.client.id,
          billId: this.data.id
        })
        // Se actualiza la fila en memoria, sin volver a leer la colección entera.
        const audit = { ...(this.data.voucherAudit || {}), ...result }
        // Evento propio y NO 'update': en "Letras pendientes" ese evento significa
        // "esta letra se pagó" y saca la fila de la lista. Una re-verificación no
        // paga nada, así que reusarlo habría hecho desaparecer la letra.
        this.$emit('audit', { id: this.data.id, voucherAudit: audit, voucherAlert: result.status === 'alert' })
        this.$toast.success(result.status === 'alert' ? 'Verificado: hay una alerta' : 'Comprobante verificado')
      } catch (err) {
        this.$toast.error(err.message || 'No se pudo verificar el comprobante')
      } finally {
        this.rechecking = false
      }
    }
  }
}
</script>

<style scoped>
.bill-detail {
  background: white;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 16px;
  border: 1px solid #ebebeb;
}

.bill-detail__amount {
  font-family: 'Inter Display', 'Inter', sans-serif;
  font-size: 28px;
  font-weight: 500;
  color: #191919;
  letter-spacing: -0.02em;
  font-feature-settings: "tnum";
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.bill-detail__amount--paid { color: #16A34A; }

.bill-detail__badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: #16A34A;
  color: white;
  padding: 3px 8px;
  border-radius: 20px;
}

.bill-detail__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding-top: 16px;
  border-top: 0.5px solid #ebebeb;
}

.bill-detail__item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.bill-detail__label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8f8f8f;
}

.bill-detail__value {
  font-size: 13px;
  font-weight: 500;
  color: #191919;
}

.bill-detail__obs {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 0.5px solid #ebebeb;
}

.bill-detail__obs p {
  font-size: 13px;
  color: #707070;
  margin-top: 4px;
  line-height: 1.4;
}

.bill-detail__diff {
  font-size: 12px;
  color: #D97706;
  margin-top: -8px;
  margin-bottom: 12px;
}

/* Resumen de diferencia en paso 2 */
.bill-detail__diff-summary {
  border-top: 0.5px solid #ebebeb;
  padding-top: 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.bill-detail__diff-row {
  display: flex;
  justify-content: space-between;
  font-size: 13px;
  color: #707070;
}

.bill-detail__diff-row--total {
  color: #191919;
  font-size: 14px;
  padding-top: 8px;
  border-top: 0.5px solid #ebebeb;
}

.pay-diff__question {
  font-size: 14px;
  color: #191919;
  line-height: 1.5;
  margin-bottom: 16px;
}
</style>
